import React from 'react';
import { Users, UserCheck, Building, Clock, ShieldCheck, ShieldAlert } from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';

interface UserStatsGridProps {
  usersList: User[];
  isLoading: boolean;
  activeFilter?: string;
  onSelectFilter?: (filter: string) => void;
}

export const UserStatsGrid: React.FC<UserStatsGridProps> = ({
  usersList,
  isLoading,
  activeFilter = 'all',
  onSelectFilter,
}) => {
  const countByRole = (role: 'penulis' | 'penerbit' | 'organisasi' | 'admin') => {
    return usersList.filter((u: User) => u.role === role).length;
  };

  const pendingPublishersCount = usersList.filter(
    (u: User) => (u.role === 'penerbit' || u.role === 'organisasi') && u.publisherVerificationStatus === 'pending'
  ).length;

  const verifiedPublishersCount = usersList.filter(
    (u: User) => u.role === 'penerbit' && (u.isPublisherVerified === 1 || u.publisherVerificationStatus === 'verified')
  ).length;

  const verifiedOrgCount = usersList.filter(
    (u: User) => u.role === 'organisasi' && (u.isPublisherVerified === 1 || u.publisherVerificationStatus === 'verified')
  ).length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      {/* Total Users */}
      <button
        type="button"
        onClick={() => onSelectFilter?.('all')}
        className={`p-3.5 sm:p-4 rounded-2xl border text-left transition shadow-2xs flex items-center gap-2.5 sm:gap-3 cursor-pointer ${
          activeFilter === 'all'
            ? 'bg-slate-900 text-white border-slate-900 ring-2 ring-slate-400/30'
            : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
        }`}
      >
        <div
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
            activeFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
          }`}
        >
          <Users className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] uppercase font-bold truncate ${activeFilter === 'all' ? 'text-slate-300' : 'text-slate-400'}`}>
            Total Akun
          </p>
          <p className="text-base sm:text-lg font-bold">{isLoading ? '...' : usersList.length}</p>
        </div>
      </button>

      {/* Pending Publisher / Org Verifications */}
      <button
        type="button"
        onClick={() => onSelectFilter?.('pending_publisher')}
        className={`p-3.5 sm:p-4 rounded-2xl border text-left transition shadow-2xs flex items-center justify-between gap-2.5 sm:gap-3 cursor-pointer ${
          activeFilter === 'pending_publisher'
            ? 'bg-amber-600 text-white border-amber-600 ring-2 ring-amber-400/30'
            : pendingPublishersCount > 0
            ? 'bg-amber-50/80 text-amber-950 border-amber-300 hover:bg-amber-100/80 ring-1 ring-amber-400/20'
            : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
        }`}
      >
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
          <div
            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
              activeFilter === 'pending_publisher'
                ? 'bg-white/20 text-white'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0">
            <p className={`text-[10px] uppercase font-bold truncate ${activeFilter === 'pending_publisher' ? 'text-amber-100' : 'text-amber-800'}`}>
              Verifikasi
            </p>
            <p className="text-base sm:text-lg font-bold">{isLoading ? '...' : pendingPublishersCount}</p>
          </div>
        </div>

        {pendingPublishersCount > 0 && activeFilter !== 'pending_publisher' && (
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping shrink-0" />
        )}
      </button>

      {/* Verified Publishers */}
      <button
        type="button"
        onClick={() => onSelectFilter?.('publisher')}
        className={`p-3.5 sm:p-4 rounded-2xl border text-left transition shadow-2xs flex items-center gap-2.5 sm:gap-3 cursor-pointer ${
          activeFilter === 'publisher'
            ? 'bg-purple-900 text-white border-purple-900 ring-2 ring-purple-400/30'
            : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
        }`}
      >
        <div
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
            activeFilter === 'publisher' ? 'bg-white/20 text-white' : 'bg-purple-50 text-purple-700'
          }`}
        >
          <Building className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] uppercase font-bold truncate ${activeFilter === 'publisher' ? 'text-purple-200' : 'text-slate-400'}`}>
            Penerbit ({verifiedPublishersCount})
          </p>
          <p className="text-base sm:text-lg font-bold">{isLoading ? '...' : countByRole('penerbit')}</p>
        </div>
      </button>

      {/* Organisasi / Komunitas */}
      <button
        type="button"
        onClick={() => onSelectFilter?.('organisasi')}
        className={`p-3.5 sm:p-4 rounded-2xl border text-left transition shadow-2xs flex items-center gap-2.5 sm:gap-3 cursor-pointer ${
          activeFilter === 'organisasi'
            ? 'bg-amber-800 text-white border-amber-800 ring-2 ring-amber-400/30'
            : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
        }`}
      >
        <div
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
            activeFilter === 'organisasi' ? 'bg-white/20 text-white' : 'bg-amber-50 text-amber-700'
          }`}
        >
          <Users className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] uppercase font-bold truncate ${activeFilter === 'organisasi' ? 'text-amber-200' : 'text-slate-400'}`}>
            Organisasi ({verifiedOrgCount})
          </p>
          <p className="text-base sm:text-lg font-bold">{isLoading ? '...' : countByRole('organisasi')}</p>
        </div>
      </button>

      {/* Authors */}
      <button
        type="button"
        onClick={() => onSelectFilter?.('penulis')}
        className={`p-3.5 sm:p-4 rounded-2xl border text-left transition shadow-2xs flex items-center gap-2.5 sm:gap-3 cursor-pointer ${
          activeFilter === 'penulis'
            ? 'bg-sky-900 text-white border-sky-900 ring-2 ring-sky-400/30'
            : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
        }`}
      >
        <div
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
            activeFilter === 'penulis' ? 'bg-white/20 text-white' : 'bg-sky-50 text-sky-700'
          }`}
        >
          <UserCheck className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] uppercase font-bold truncate ${activeFilter === 'penulis' ? 'text-sky-200' : 'text-slate-400'}`}>
            Penulis Mandiri
          </p>
          <p className="text-base sm:text-lg font-bold">{isLoading ? '...' : countByRole('penulis')}</p>
        </div>
      </button>
    </div>
  );
};
